// ─── Draft Key ────────────────────────────────────────────────────────────────

/**
 * Parameters used to construct a namespaced draft storage key.
 *
 * Scoping by workshopId + userId ensures drafts from one user/workshop never
 * appear in another — critical for multi-tenant ERP.
 *
 * @example
 * buildDraftKey({
 *   module:      'job-cards',
 *   entityType:  'job-card',
 *   entityId:    undefined,   // new record
 *   userId:      'u-123',
 *   workshopId:  'ws-456',
 * })
 * // → 'erp-draft:ws-456:u-123:job-cards:job-card:new'
 */
export interface DraftKeyParams {
  /** ERP module name.  e.g. 'job-cards', 'customers', 'billing' */
  module: string;
  /** Entity type within the module. e.g. 'job-card', 'customer', 'invoice-line' */
  entityType: string;
  /**
   * Entity ID when editing an existing record.
   * Omit (or pass `undefined`) for new-record drafts.
   */
  entityId?: string;
  /** Current user ID — scopes draft to the user. Defaults to '_anon'. */
  userId?: string;
  /** Current workshop ID — scopes draft to the workshop. Defaults to '_global'. */
  workshopId?: string;
}

// ─── Draft Metadata ───────────────────────────────────────────────────────────

/**
 * Metadata stored alongside every draft.
 * Exposed to consumers for UI (e.g. "Last saved 2 minutes ago").
 */
export interface DraftMetadata {
  /** Unix timestamp (ms) when this draft was last saved. */
  savedAt: number;
  /** Unix timestamp (ms) after which this draft should be pruned. */
  expiresAt: number;
  /** Monotonically increasing counter — incremented on every save. */
  version: number;
  /**
   * A random UUID generated when the draft session starts.
   * Stable across saves within the same form session. Reused on restore.
   */
  sessionId: string;
}

// ─── Draft Entry ─────────────────────────────────────────────────────────────

/**
 * The full record stored in the adapter for a single draft key.
 */
export interface DraftEntry<T = unknown> {
  /** Preserved form data. */
  data: T;
  /** Metadata for expiry, versioning, and display. */
  meta: DraftMetadata;
}

// ─── Storage Adapter ─────────────────────────────────────────────────────────

/**
 * Pluggable storage backend for the DraftRegistry.
 *
 * Three built-in adapters are provided:
 * - `LocalStorageAdapter`   — persists across browser sessions (default)
 * - `SessionStorageAdapter` — persists for the current browser tab only
 * - `MemoryStorageAdapter`  — in-process only, useful for testing
 *
 * Custom adapters (IndexedDB, server-side) can be implemented by satisfying
 * this interface.
 */
export interface StorageAdapter {
  /** Retrieve a draft entry by key. Returns null if not found. */
  get<T>(key: string): DraftEntry<T> | null;
  /** Persist a draft entry. */
  set<T>(key: string, entry: DraftEntry<T>): void;
  /** Remove a draft entry. No-op if not found. */
  remove(key: string): void;
  /**
   * Return all stored draft keys, optionally filtered by a prefix.
   * Used by `pruneExpired()` to scan for stale entries.
   */
  keys(prefix?: string): string[];
  /** Remove all draft entries matching an optional prefix. */
  clear(prefix?: string): void;
}

// ─── Options ──────────────────────────────────────────────────────────────────

/**
 * Options for a single `registry.save()` call.
 */
export interface SaveOptions {
  /**
   * Time-to-live in milliseconds from now.
   * Overrides the registry's `defaultExpiryMs` for this specific save.
   */
  expiryMs?: number;
}

/**
 * Options for the DraftRegistry constructor.
 */
export interface DraftRegistryOptions {
  store: StorageAdapter;
  /** Debounce delay in ms for `saveDebounced()`. Default: 800 ms. */
  debounceMs?: number;
  /** Default TTL for drafts in ms. Default: 7 days. */
  defaultExpiryMs?: number;
}

/**
 * Options for `useDraft()`.
 */
export interface UseDraftOptions {
  /** TTL override for saves from this hook. */
  expiryMs?: number;
  /** Debounce delay override for `saveDebounced` from this hook. Unused — registry controls debounce. */
  debounceMs?: number;
}

/**
 * Options for `useFormDraft()`.
 */
export interface UseFormDraftOptions {
  /** TTL override. */
  expiryMs?: number;
  /**
   * If true, the draft is automatically restored to the form when the
   * component mounts AND a draft exists. Default: false.
   *
   * Set to false to let the consumer show a "Restore?" banner first.
   */
  autoRestore?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Prefix prepended to all draft keys in storage. */
export const DRAFT_KEY_PREFIX = 'erp-draft';

/** Default draft TTL: 7 days in milliseconds. */
export const DEFAULT_DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

/** Default autosave debounce delay: 800 ms. */
export const DEFAULT_DEBOUNCE_MS = 800;

/** Interval for pruning expired drafts: 10 minutes. */
export const PRUNE_INTERVAL_MS = 10 * 60 * 1000;
